import * as THREE from 'three';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

const settings = window.scrollSettings;


if (settings.template === 'template-2') {


    // prevent browser from restoring scroll on reload/back
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';


    // Make sure we always start on top
    //document.documentElement.scrollTop = 0;
    window.addEventListener("DOMContentLoaded", function() {
        setTimeout(function() {
            window.scrollTo(0,0);
        }, 0);
    });

    let scene, camera, renderer, starGeo, stars, tiltGroup; // ← tiltGroup

    let velocities, accelerations; // per-star data

    const N = 6000;
    const yMin = -200, yMax = 200;      // your old bounds
    const FADE_DIST = 30;
    const range = 600;

    // scroll-driven forward speed (along -Y) with friction
    let forwardSpeed = 0;               // world units/frame
    const friction   = 0.92;            // 0..1
    const speedScale = 0.0012;          // px/sec → world units
    const maxSpeed   = 6;

    // --- tilt config ---
    const maxRoll  = 0.15;              // ~8.6° (left/right) around Z
    const maxPitch = 0.08;              // ~4.6° (up/down) around X
    let rollTo, pitchTo;                // gsap quickTo setters
    let domTiltEl, domParallaxEls = [];
    const parallaxBase = 20; // px movement for data-parallax="1"
    let layerSetters = [];   // built from [data-parallax] nodes

    const flight = { thrust: 0 }; // extra speed (world units/frame) controlled by GSAP


    function createDotTexture() {
        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        const center = size / 2;
        const gradient = ctx.createRadialGradient(center, center, 0, center, center, center);
        gradient.addColorStop(0, 'rgba(255,255,255,1)');
        gradient.addColorStop(0.5, 'rgba(255,255,255,0.95)');
        gradient.addColorStop(1, 'rgba(255,255,255,0)');

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, size, size);

        const texture = new THREE.CanvasTexture(canvas);
        texture.generateMipmaps = false;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.needsUpdate = true;

        return texture;
    }



    function init() {
        scene = new THREE.Scene();


        // Your top-down camera (so “forward” is along –Y)
        camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 1, 1000);
        camera.position.z = 1;
        camera.rotation.x = Math.PI / 2;

        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        document.getElementById('three-space').appendChild(renderer.domElement);

        // --- geometry & attributes ---
        const positions = new Float32Array(N * 3);
        for (let i = 0; i < N; i++) {
            positions[i*3 + 0] = Math.random() * range - range / 2;               // x
            positions[i*3 + 1] = Math.random() * (yMax - yMin) + yMin;            // y
            positions[i*3 + 2] = Math.random() * range - range / 2;               // z
        }

        starGeo = new THREE.BufferGeometry();
        starGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        starGeo.attributes.position.setUsage(THREE.DynamicDrawUsage);

        const colors = new Float32Array(N * 3);
        // start dark; will brighten as they fall
        starGeo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        starGeo.computeBoundingSphere();

        // per-star velocity/acceleration
        velocities = new Float32Array(N);
        accelerations = new Float32Array(N);
        for (let i = 0; i < N; i++) {
            velocities[i] = 0;
            accelerations[i] = 0.0002;
        }

        const sprite = createDotTexture();

        const starMaterial = new THREE.PointsMaterial({
            color: 0xaaaaaa,
            size: 1,
            map: sprite,
            transparent: true,
            alphaTest: 0.01,
            depthWrite: false,
            sizeAttenuation: true, // perspective sizing (so not all same size)
            vertexColors: true
        });

        stars = new THREE.Points(starGeo, starMaterial);

        // --- wrap stars in a tilt group and add to scene ---
        tiltGroup = new THREE.Group();
        tiltGroup.add(stars);
        scene.add(tiltGroup);

        // --- DOM nodes to react to mouse (adjust selectors to your markup) ---
        domParallaxEls = Array.from(document.querySelectorAll('.move')); // multiple layers

        // --- GSAP setters for smooth scene tilt & DOM transforms ---
        rollTo  = gsap.quickTo(tiltGroup.rotation, 'z', { duration: 1, ease: 'power2.out' });
        pitchTo = gsap.quickTo(tiltGroup.rotation, 'x', { duration: 1, ease: 'power2.out' });


        // --- DOM tilt: use quickTo so it animates (DOM rotation uses DEGREES) ---
        const rollMaxDeg  = THREE.MathUtils.radToDeg(maxRoll);
        const pitchMaxDeg = THREE.MathUtils.radToDeg(maxPitch);

        const domRollTo  = domTiltEl ? gsap.quickTo(domTiltEl, 'rotation',  { duration: 0.45, ease: 'power2.out' }) : null;
        const domPitchTo = domTiltEl ? gsap.quickTo(domTiltEl, 'rotationX', { duration: 0.45, ease: 'power2.out' }) : null;

        // --- DOM parallax layers: animate x/y with quickTo too ---
        const layerTweens = domParallaxEls.map(el => ({
            xTo: gsap.quickTo(el, 'x', { duration: 0.45, ease: 'power3.out' }),
            yTo: gsap.quickTo(el, 'y', { duration: 0.45, ease: 'power3.out' }),
            depth: parseFloat(el.dataset.parallax) || 1
        }));

        layerSetters = domParallaxEls.map(el => ({
            x: gsap.quickSetter(el, 'x', 'px'),
            y: gsap.quickSetter(el, 'y', 'px'),
            depth: parseFloat(el.dataset.parallax) || 1
        }));

        // --- pointer listeners on WINDOW so elements outside canvas react too ---
        function handlePointerMove(e) {
            const nx = (e.clientX / window.innerWidth)  * 2 - 1; // -1..1
            const ny = (e.clientY / window.innerHeight) * 2 - 1; // -1..1

            // Scene tilt (camera pitched +π/2 → L/R = roll Z, U/D = pitch X)
            rollTo(-nx * maxRoll);
            pitchTo(ny * maxPitch);

            // DOM tilt (degrees)
            if (domRollTo)  domRollTo(-nx * rollMaxDeg);
            if (domPitchTo) domPitchTo(ny * pitchMaxDeg);

            // DOM parallax (translate in px)
            for (const { xTo, yTo, depth } of layerTweens) {
                const amt = parallaxBase * depth; // e.g. 20px * depth
                xTo(nx * amt);
                yTo(ny * amt);
            }
        }

        function handlePointerLeave() {
            // Smoothly go back to neutral for both scene and DOM
            rollTo(0);
            pitchTo(0);
            if (domRollTo)  domRollTo(0);
            if (domPitchTo) domPitchTo(0);
            for (const { xTo, yTo } of layerTweens) { xTo(0); yTo(0); }
        }

        // Attach once mouse movement
        /*
        window.addEventListener('pointermove', handlePointerMove, { passive: true });
        window.addEventListener('mouseout', (e) => {
            if (!e.relatedTarget || e.relatedTarget.nodeName === 'HTML') handlePointerLeave();
        });

         */

        window.addEventListener('resize', onResize);


        // Add class to body
        document.documentElement.classList.add('scroll-block', 'hero-template2');

        gsap.set('.hand-mouth', { opacity: 0 } );
        gsap.set('.elementor-widget-webien_hero_scroll .section-1', { opacity: 1, scale: 0 } );
        gsap.set('.elementor-widget-webien_hero_scroll .section-2', { opacity: 1, scale: 0 } );

        const delay1 = settings.delay_template2_1 || 1;
        const delay2 = settings.delay_template2_2 || 3;

        // Hide late heading if present
        jQuery('.late-heading').hide();
        // When user has scrolled past the hero section (100vh) show late heading
        ScrollTrigger.create({
            trigger: '#space-wrap',
            start: 'top top',
            end: 'bottom top',
            onLeave: () => {
                jQuery('.late-heading').show();
            },
        } );

        // If mobile, scale up hands a bit from start
        if (window.innerWidth < 600) {
            gsap.set('.hand-mouth',  { scale: 1.75, transformOrigin: '58% 50%' } );
        }

        const uiTl = gsap.timeline({ paused: true, defaults: { ease: 'none' } })
            .to('.hand-mouth', {opacity: 1, duration: 0.3})
            .to('.hand-mouth', {opacity: 1, duration: delay1})
            .add('start')
            .to('.hand-right', { rotation: 20, xPercent: 10,  transformOrigin: '90% 85%', duration: 1 }, 'start')
            .to('.hand-left', { rotation: -20, xPercent: -10,  transformOrigin: '19% 77%', duration: 1 }, 'start')
            .to(flight,{ thrust: 3, duration: 1.5, ease: 'power2.out' }, 'start')
            .to('.hand-mouth', { scale: 10, opacity:0, ease: 'power4.in', transformOrigin: '58% 50%', duration: 1, delay: 0.5 }, 'start')
            .to(flight,{ thrust: 0.2, duration: 0.8, ease: 'power3.in' }, '>-0.1')
            .to('.section-1',  { scale:1, ease: 'back.out', duration: 1 }, '<60%')
            .add('section1')
            .to('.section-1', {scale: 100, opacity:0, ease: 'power4.in', duration: 1}, 'section1+=' + delay2)
            .to(flight,{ thrust: 3, duration: 1, ease: 'power2.out' }, 'section1+=' + delay2)
            .add('section2in')
            .to('.section-2', { scale:1, ease: 'back.out', duration: 1 })
            .to(flight, { thrust: 0.2, duration: 1, ease: 'power2.out' }, 'section2in')
            .add( function(){
                document.documentElement.classList.remove('scroll-block');
            })
            .to('.section-2', { opacity:1, duration: 2 })
            .to(flight, { thrust: 0, duration: 3, ease: 'none' })


        // If the existence of cookiebot exit, wait until the user has made a choice befopre playing the timeline
        if (window.Cookiebot) {
            uiTl.seek("start");
            window.addEventListener('CookiebotOnAccept', function() {
                uiTl.play();
            });
            window.addEventListener('CookiebotOnDecline', function() {
                uiTl.play();
            });
            // If existing consent exists, play immediately
            if (window.Cookiebot.hasResponse) {
                uiTl.play();
            }
        } else {
            uiTl.play();
        }


/*
        const PX_PER_SEC = 600;

        // ScrollTrigger: nudge forwardSpeed by scroll velocity (unchanged)
        ScrollTrigger.create({
            trigger: '#three-space',
            start: 'top top',
            end: () => '+=' + (uiTl.totalDuration() * PX_PER_SEC),
            pin: true,
            scrub: 0.5,
            animation: uiTl,
            markers: true, // optional
            onUpdate(self) {
                const v = self.getVelocity() * speedScale; // px/sec → world units
                forwardSpeed = THREE.MathUtils.clamp(forwardSpeed + v, -maxSpeed, maxSpeed);

                // also scrub the UI timeline to match the scrollbar position
                uiTl.progress(self.progress);
            },
            onLeave() {

            }
        });

 */





        animate();
    }

    function onResize() {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
    }

    function animate() {
        requestAnimationFrame(animate);

        // inertia: slow down when not scrolling
        forwardSpeed *= friction;
        if (Math.abs(forwardSpeed) < 0.0001) forwardSpeed = 0;

        const pos = starGeo.attributes.position;
        const col = starGeo.attributes.color;
        const pArr = pos.array;
        const cArr = col.array;

        for (let i = 0; i < N; i++) {
            // your original Y motion + scroll-driven speed along -Y
            velocities[i] += accelerations[i];
            const yIdx = i*3 + 1;
            pArr[yIdx] -= (velocities[i] + forwardSpeed + flight.thrust);


            if (pArr[yIdx] < yMin) {
                pArr[yIdx] = yMax;
                velocities[i] = 0;
                const cIdx = i*3;
                cArr[cIdx] = cArr[cIdx+1] = cArr[cIdx+2] = 0; // invisible at spawn
            } else {
                const travelled = yMax - pArr[yIdx];
                let f = travelled / FADE_DIST;
                if (f > 1) f = 1;
                // smoothstep
                f = f * f * (3 - 2 * f);
                const cIdx = i*3;
                cArr[cIdx] = cArr[cIdx+1] = cArr[cIdx+2] = f;
            }
        }

        pos.needsUpdate = true;
        col.needsUpdate = true;

        renderer.render(scene, camera);
    }

    window.addEventListener('load', async () => {
        await scrollToTopAndWait(window, { smooth: true }); // or {smooth:false} to force instant
        init();
    });


}







// Await scrolling to the top. If CSS has scroll-behavior:smooth, it’ll be smooth.
// Pass {smooth:false} to force instant even if CSS says smooth.
function scrollToTopAndWait(target = window, { smooth = true, timeout = 3000 } = {}) {
    return new Promise((resolve) => {
        const isWin = target === window;
        const getY  = () => (isWin ? window.scrollY || window.pageYOffset : target.scrollTop);

        // already at top
        if (getY() === 0) return resolve();

        const supportsSmooth = 'scrollBehavior' in document.documentElement.style;
        const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const behavior = smooth && supportsSmooth && !prefersReduced ? 'smooth' : 'auto';

        let rafId, toId;
        const cleanup = () => {
            if (rafId) cancelAnimationFrame(rafId);
            if (toId) clearTimeout(toId);
            // `scrollend` is on Document/Element in modern browsers
            const endTarget = isWin ? document : target;
            endTarget.removeEventListener('scrollend', onEnd, true);
        };

        const finish = () => { cleanup(); resolve(); };

        const onEnd = () => { if (getY() === 0) finish(); };

        // Try native scrollend if present
        const endTarget = isWin ? document : target;
        endTarget.addEventListener('scrollend', onEnd, { passive: true });

        // rAF fallback: resolve when we hit 0 (or stop moving for a few frames)
        let still = 0, lastY = getY();
        const tick = () => {
            const y = getY();
            if (y === 0) return finish();
            // If no change for ~6 frames (~100ms @60fps), assume done (covers non-smooth/blocked)
            if (y === lastY) { if (++still > 6) return finish(); }
            else { still = 0; lastY = y; }
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        // Safety net
        toId = setTimeout(finish, timeout);

        // Kick it off
        if (isWin) {
            window.scrollTo({ top: 0, left: 0, behavior });
        } else {
            target.scrollTo({ top: 0, left: 0, behavior });
        }
    });
}
